import { fetchDashboardList, fetchDashboardLayout } from './dashboardService';
import { fetchWidgetData } from './widgetDataService';

/**
 * Default data adapters for WidgetGrid (internal mock implementations).
 *
 * These adapters are used automatically when no custom `dataAdapters` prop
 * is passed to `WidgetGridProvider`. They provide mock data for development
 * and Storybook demos.
 *
 * To connect to a real API, pass a custom object with the same shape to
 * `WidgetGridProvider`'s `dataAdapters` prop. You can replace all or just
 * some adapters by spreading the defaults:
 *
 * @example
 * import { defaultDataAdapters } from '@aimsos/ui';
 *
 * const myAdapters = {
 *   ...defaultDataAdapters,
 *   fetchDashboardLayout: async (dashboardUuid, options) => {
 *     const res = await fetch(`/api/dashboards/${dashboardUuid}`, {
 *       signal: options?.signal,
 *     });
 *     return { data: await res.json() };
 *   },
 * };
 *
 * <WidgetGridProvider dataAdapters={myAdapters}>
 *   <WidgetGrid dashboardUuid="dash-1" />
 * </WidgetGridProvider>
 *
 * Contract (each function must match this signature):
 *
 * @property {Function} fetchDashboardList
 *   (scopeType, module) => Promise<{ data: Dashboard[] }>
 *
 * @property {Function} fetchDashboardLayout
 *   (dashboardUuid, options?) => Promise<{ data: DashboardLayout }>
 *   options.signal — AbortSignal for request cancellation
 *
 * @property {Function} fetchWidgetData
 *   (instanceUuid, scopeContext, dashboardContext, widgetContext?, options?) => Promise<{ data, meta }>
 *   options.signal — AbortSignal for request cancellation
 */
const defaultDataAdapters = Object.freeze({
  fetchDashboardList,
  fetchDashboardLayout,
  fetchWidgetData,
});

export default defaultDataAdapters;
